import React, { useCallback, useMemo, useState } from "react";
import { useEffect } from "react";
import { createPortal } from "react-dom";
import Select, { MenuProps, components } from "react-select";
import toast from "react-hot-toast";
import { DA_Button } from "@danishagro/shared/src/components/atoms/Button/Button.component";
import { DA_Image } from "@danishagro/shared/src/components/atoms/Image/Image.component";
import { DA_InputWrapper } from "@danishagro/shared/src/components/atoms/InputWrapper/InputWrapper.component";
import { DA_Modal } from "@danishagro/shared/src/components/molecules/Modal/Modal.component";
import { DA_QuantityPicker } from "@molecules/QuantityPicker/QuantityPicker.component";
import { DA_Select } from "@danishagro/shared/src/components/atoms/Select/Select.component";
import { DA_Icon, DA_IconNames } from "@danishagro/shared/src/components/atoms/Icon/Icon.component";
import { DA_DropdownArrow } from "@danishagro/shared/src/components/atoms/DropdownArrow/DropdownArrow.component";
import { DA_AddressForm } from "@danishagro/shared/src/components/organisms/AddressForm/AddressForm.component";
import { useFarmInTimeApi } from "@hooks/api/useFarmInTimeApi.hook";
import { useAppData } from "@danishagro/shared/src/contexts/appData.context";
import { useModal } from "@danishagro/shared/src/contexts/modal.context";
import { useScreen } from "@danishagro/shared/src/contexts/screen.context";
import { useTranslations } from "@danishagro/shared/src/contexts/translations/translations.context";
import { DA_WishListToast } from "@molecules/WishListButtonSiloAndHouse/WishListToast/WishListToast.component";
import { useAddressFromSessionStorage } from "@danishagro/shared/src/hooks/useAddressFromSessionStorage.hook";
import { pushToDataLayer } from "@danishagro/shared/src/gtm/useTagManger";
import { B2bImageSrc } from "@danishagro/shared/src/helpers/imageSrc.helper";
import { ImageConfig } from "@danishagro/shared/src/content/imageConfigs/imageConfig.enum";
import { DA_Tag } from "@danishagro/shared/src/components/atoms/Tag/Tag.component";
import { isGreaterThanZero } from "@danishagro/shared/src/helpers/greaterThanZero.helper";
import {
    DA_DeliveryAddress,
    DA_DeliveryPoint,
} from "@danishagro/shared/src/hooks/api/useDeliveryPointsApi.hook";
import { useDeliveryPointsApi } from "@danishagro/shared/src/hooks/api/useDeliveryPointsApi.hook";
import { DA_SelectOption } from "@danishagro/shared/src/components/atoms/Select/Select.props";
import { DA_Address } from "@danishagro/shared/src/interfaces/address.interface";
import { getSelectStyles } from "@danishagro/shared/src/helpers/getSelectStyles.helper";
import { cn } from "@danishagro/shared/src/helpers/classNames.helper";
import { useDeliveryPointSelectHandlers } from "@hooks/useDeliveryPointSelectHandlers.hook";
import { formatNumber } from "@danishagro/shared/src/helpers/formatNumber.helper";
import Skeleton from "react-loading-skeleton";
import { useMonths } from "../hooks/useMonths.hook";
import { useFrequencies } from "../hooks/useFrequencies.hook";
import S from "./FarmInTimeModal.module.scss";

interface FarmInTimeModalProps {
    productId: string;
    productName: string;
    productImage: string;
    variantId?: string;
    quantityOnPallet?: string;
    productNumber?: string;
    price?: number;
    minimumSalesQuantity?: string;
}

export const _FarmInTimeModal = (props: FarmInTimeModalProps) => {
    const { getDictionaryString, getDictionaryItem } = useTranslations();
    const { closeModal } = useModal();
    const { customerNumber, currentCulture } = useAppData();
    const [quantity, setQuantity] = useState(parseInt(props.minimumSalesQuantity) || 1);
    const { isMobile } = useScreen();
    const { setFarmInTimeProductsV3 } = useFarmInTimeApi();
    const { getDeliveryPoints } = useDeliveryPointsApi();
    const deliveryPointHandlers = useDeliveryPointSelectHandlers();

    // Delivery points state
    const [deliveryAddresses, setDeliveryAddresses] = useState<DA_DeliveryAddress[]>([]);
    const [selectedDeliveryAddress, setSelectedDeliveryAddress] = useState<string>("");
    const [selectedDeliveryPoint, setSelectedDeliveryPoint] =
        useState<string>("standard-delivery-point");
    const [deliveryPointsLoading, setDeliveryPointsLoading] = useState(false);

    // Address management state
    const [checkoutAddresses, setCheckoutAddresses] = useState<DA_Address[]>([]);
    const [showAddressModal, setShowAddressModal] = useState(false);
    const [isAddressDropdownOpen, setIsAddressDropdownOpen] = useState(false);

    const { setAddressInSessionStorage } = useAddressFromSessionStorage();

    // Custom Menu component for delivery address dropdown with "Add manual address" option
    const DeliveryAddressMenu = (props: MenuProps<DA_SelectOption>) => {
        return (
            <components.Menu {...props}>
                <React.Fragment>
                    {props.children}
                    <hr className={S.addAddressMenuSeparator} />
                    <div
                        className={S.addAddressMenuOption}
                        role="button"
                        tabIndex={0}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (props.selectProps.onMenuClose) {
                                props.selectProps.onMenuClose();
                            }
                            setTimeout(() => {
                                handleManualAddressSelection(e);
                            }, 100);
                        }}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                e.stopPropagation();
                                if (props.selectProps.onMenuClose) {
                                    props.selectProps.onMenuClose();
                                }
                                setTimeout(() => {
                                    handleManualAddressSelection(e);
                                }, 100);
                            }
                        }}
                        onTouchEnd={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (props.selectProps.onMenuClose) {
                                props.selectProps.onMenuClose();
                            }
                            setTimeout(() => {
                                handleManualAddressSelection(e);
                            }, 100);
                        }}
                    >
                        {getDictionaryString("siloAndHouses.addManualAddress")}
                    </div>
                </React.Fragment>
            </components.Menu>
        );
    };

    // Handle manual address selection
    const handleManualAddressSelection = (
        e?: React.MouseEvent | React.KeyboardEvent | React.TouchEvent
    ) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        setShowAddressModal(true);
    };

    // Handle address form submission
    const onAddressFormSubmitted = useCallback((newAddress: DA_Address) => {
        setCheckoutAddresses((prev) => [...prev, newAddress]);
        setShowAddressModal(false);
    }, []);

    // Sync checkout addresses with delivery addresses when they change
    useEffect(() => {
        if (checkoutAddresses.length > 0) {
            const latestAddress = checkoutAddresses[checkoutAddresses.length - 1];
            const newDeliveryAddress: DA_DeliveryAddress = {
                id: latestAddress.id,
                locationId: latestAddress.locationId,
                streetName: latestAddress.streetName,
                streetNumber: latestAddress.streetNumber,
                zipCode: latestAddress.zipCode,
                city: latestAddress.city,
                countryCode: latestAddress.countryCode,
                isPrimary: false,
                description: latestAddress.description || "",
                deliveryPoints: [],
            };

            setDeliveryAddresses((prevAddresses) => {
                const addressExists = prevAddresses.some(
                    (addr) => addr.locationId === newDeliveryAddress.locationId
                );
                if (addressExists) {
                    return prevAddresses;
                }
                return [...prevAddresses, newDeliveryAddress];
            });

            setSelectedDeliveryAddress(newDeliveryAddress.id);
            setSelectedDeliveryPoint("standard-delivery-point");
        }
    }, [checkoutAddresses]);

    // Fetch delivery points on component mount
    useEffect(() => {
        if (customerNumber && props.productNumber) {
            setDeliveryPointsLoading(true);
            getDeliveryPoints(customerNumber, null, props.productNumber)
                .then((addresses) => {
                    setDeliveryAddresses(addresses);
                    // Auto-select first address if available
                    if (addresses.length > 0) {
                        const primaryAddress =
                            addresses.find((addr) => addr.isPrimary) || addresses[0];
                        setSelectedDeliveryAddress(primaryAddress.id); // Use full id
                    }
                })
                .catch((error) => {
                    console.error("Failed to fetch delivery addresses:", error);
                })
                .finally(() => {
                    setDeliveryPointsLoading(false);
                });
        }
    }, [customerNumber, props.productNumber, getDeliveryPoints]);

    // Helper function to format address for display
    const formatAddressForLabel = (address: DA_DeliveryAddress): string => {
        const parts = [
            address.streetName && address.streetNumber
                ? `${address.streetName} ${address.streetNumber}`
                : address.streetName || address.streetNumber,
            address.zipCode && address.city
                ? `${address.zipCode} ${address.city}`
                : address.zipCode || address.city,
        ].filter(Boolean);
        return parts.join(", ");
    };

    // Get delivery points for selected address
    const getDeliveryPointsForSelectedAddress = () => {
        const selectedAddress = deliveryAddresses.find(
            (addr) => addr.id === selectedDeliveryAddress
        );
        return selectedAddress?.deliveryPoints || [];
    };

    // Generate address dropdown options
    const addressOptions: DA_SelectOption[] = deliveryAddresses
        .sort((a, b) => {
            if (a.isPrimary && !b.isPrimary) return -1;
            if (!a.isPrimary && b.isPrimary) return 1;
            return 0;
        })
        .map((address) => ({
            value: address.id, // Use full id instead of locationId
            label: formatAddressForLabel(address),
        }));

    // Generate delivery point dropdown options
    type DeliveryPointOption =
        | DA_DeliveryPoint
        | { id: string; name: string; description?: string };

    const deliveryPointOptions: DeliveryPointOption[] = [
        {
            id: "standard-delivery-point",
            name: getDictionaryString("siloAndHouses.standardDeliveryPoint"),
            description: "",
        },
        ...getDeliveryPointsForSelectedAddress(),
    ];

    // Handle address selection change
    const handleAddressChange = (selectedOption: DA_SelectOption) => {
        setSelectedDeliveryAddress(selectedOption.value as string);
        setSelectedDeliveryPoint("standard-delivery-point");
    };

    // Handle delivery point selection change
    const handleDeliveryPointChange = (deliveryPoint: DeliveryPointOption) => {
        setSelectedDeliveryPoint(deliveryPoint.id);
    };

    const updateQuantity = useCallback((newQuantity: number) => {
        setQuantity(newQuantity);
    }, []);

    const addProductToFarmInTime = useCallback(
        (productId, quantity, variantId, frequency, month, addressId, deliveryPointId) => {
            const productData: {
                productId: string;
                quantity: number;
                variantId: string;
                frequency: string;
                month: string;
                placementId?: string;
            } = {
                productId: productId,
                quantity: quantity,
                variantId: variantId || "",
                frequency: frequency,
                month: month,
            };

            // Set placementId: empty string for standard delivery point, actual ID for specific delivery points
            if (deliveryPointId === "standard-delivery-point") {
                productData.placementId = "";
            } else if (deliveryPointId) {
                productData.placementId = deliveryPointId;
            }

            setFarmInTimeProductsV3(customerNumber, addressId, productData).then(() => {
                toast.custom(() => (
                    <DA_WishListToast
                        heading={getDictionaryString("WishListToastFarmInTimeHeading")}
                        href="/bestillingsliste#tab-FARM-IN-TIME"
                    />
                ));
                setAddressInSessionStorage(addressId);
                // GTM Tracking
                pushToDataLayer({
                    event: "add_as_favorite",
                    product_name: props.productName,
                });
                closeModal();
            });
        },
        [
            setFarmInTimeProductsV3,
            customerNumber,
            setAddressInSessionStorage,
            getDictionaryString,
            props.productName,
            closeModal,
        ]
    );

    const isFormReady = useMemo(
        () => selectedDeliveryAddress !== "" && !deliveryPointsLoading,
        [selectedDeliveryAddress, deliveryPointsLoading]
    );

    const { frequencyOptions, onFrequencySelectChanged, currentFrequency, currentFrequencyOption } =
        useFrequencies();

    const {
        monthOptions,
        onMonthSelectChanged,
        currentMonth,
        currentMonthOption,
        hasMonthError,
        setMonthError,
    } = useMonths();

    const onSubmitButtonClicked = () => {
        if (currentFrequency === "Yearly" && typeof currentMonth === "undefined") {
            setMonthError(true);
            return;
        }

        addProductToFarmInTime(
            props.productId,
            quantity,
            props.variantId,
            currentFrequency,
            currentMonth,
            selectedDeliveryAddress,
            selectedDeliveryPoint
        );
    };

    return (
        <DA_Modal
            title={getDictionaryString("AddProductToFarmInTime", {
                uppercaseFirst: true,
            })}
        >
            <div className={cn(S.content, isMobile && S.mobileContent)}>
                <div className={S.leftSide}>
                    {/* Product Image */}
                    <div className={S.imageWrapper}>
                        <DA_Image
                            key={props.productImage}
                            src={B2bImageSrc(props.productImage, ImageConfig.CategoryImage)}
                            alt={props.productName}
                            width={150}
                            height={150}
                            className={S.image}
                        />
                    </div>

                    {/* Product Name */}
                    <div className={S.productNumber}>
                        {getDictionaryString("product number")} {props.productNumber}
                    </div>
                    {/* Product Name */}
                    <div className={S.productName}>{props.productName}</div>

                    {/* Price */}
                    <div className={S.price}>
                        {props.price !== undefined && props.price !== null && props.price > 0 ? (
                            formatNumber(props.price, currentCulture, {
                                decimals: 2,
                            })
                        ) : (
                            <Skeleton width={80} height={20} />
                        )}
                    </div>

                    {/* Quantity Picker */}
                    <div className={S.quantityWrapper}>
                        <DA_QuantityPicker
                            onChange={updateQuantity}
                            value={quantity}
                            minQty={props.minimumSalesQuantity}
                        />
                        {isGreaterThanZero(props.quantityOnPallet) && (
                            <div className={S.labelTagWrapper}>
                                <DA_Tag color="neutral" size="dynamic">
                                    {getDictionaryItem("quantityonpallettext", {
                                        quantity: props.quantityOnPallet,
                                    })}
                                </DA_Tag>
                            </div>
                        )}
                    </div>
                </div>

                <div className={S.rightSide}>
                    {/* Address and Delivery Point Selection */}
                    <div className={S.addressAndPlacementWrapper}>
                        <div className={S.addressAndPlacementBox}>
                            <div className={S.addressAndPlacementBoxItem}>
                                <DA_InputWrapper
                                    label={getDictionaryString("siloAndHouses.deliveryAddress")}
                                    forId="add-product-to-fit-address"
                                >
                                    <Select<DA_SelectOption>
                                        options={addressOptions}
                                        value={
                                            addressOptions.find(
                                                (option) => option.value === selectedDeliveryAddress
                                            ) || null
                                        }
                                        onChange={(selectedOption) =>
                                            selectedOption && handleAddressChange(selectedOption)
                                        }
                                        components={{
                                            Menu: DeliveryAddressMenu,
                                            DropdownIndicator: (data) => (
                                                <DA_DropdownArrow {...data} />
                                            ),
                                            IndicatorSeparator: () => null,
                                        }}
                                        styles={getSelectStyles({
                                            isMobile: isMobile,
                                            isGrouped: false,
                                        })}
                                        menuPortalTarget={document.body}
                                        menuPlacement="auto"
                                        menuIsOpen={isAddressDropdownOpen}
                                        onMenuOpen={() => {
                                            setIsAddressDropdownOpen(true);

                                            // Scroll to selected option after menu opens
                                            setTimeout(() => {
                                                // Try multiple selectors since menu is in portal
                                                const menuList =
                                                    (document.querySelector(
                                                        '[class*="MenuList"]'
                                                    ) as HTMLElement) ||
                                                    (document.querySelector(
                                                        '[class*="menu-list"]'
                                                    ) as HTMLElement) ||
                                                    (document.querySelector(
                                                        '.css-26l3qy-menu [class*="MenuList"]'
                                                    ) as HTMLElement);

                                                const selectedElement =
                                                    (document.querySelector(
                                                        '[class*="option--is-selected"]'
                                                    ) as HTMLElement) ||
                                                    (document.querySelector(
                                                        '[aria-selected="true"]'
                                                    ) as HTMLElement);

                                                if (menuList && selectedElement) {
                                                    // Calculate scroll position to center the selected item
                                                    const scrollTop =
                                                        selectedElement.offsetTop -
                                                        menuList.clientHeight / 2 +
                                                        selectedElement.clientHeight / 2;
                                                    menuList.scrollTop = Math.max(0, scrollTop);
                                                }
                                            }, 100);
                                        }}
                                        onMenuClose={() => setIsAddressDropdownOpen(false)}
                                        isSearchable={true}
                                        placeholder={
                                            deliveryPointsLoading
                                                ? getDictionaryString(
                                                      "siloAndHouses.loadingAddresses"
                                                  )
                                                : addressOptions.length === 0
                                                ? getDictionaryString(
                                                      "siloAndHouses.noAddressesAvailable"
                                                  )
                                                : getDictionaryString(
                                                      "siloAndHouses.selectDeliveryAddress"
                                                  )
                                        }
                                        isDisabled={deliveryPointsLoading}
                                        id="add-product-to-fit-address"
                                    />
                                </DA_InputWrapper>
                            </div>
                            <div className={S.addressAndPlacementBoxItem}>
                                <DA_InputWrapper
                                    label={getDictionaryString("siloAndHouses.deliveryPoint")}
                                    forId="add-product-to-fit-delivery-point"
                                >
                                    <DA_Select
                                        id="add-product-to-fit-delivery-point"
                                        value={selectedDeliveryPoint}
                                        options={deliveryPointOptions}
                                        onChange={handleDeliveryPointChange}
                                        {...deliveryPointHandlers}
                                        disabled={
                                            deliveryPointsLoading || selectedDeliveryAddress === ""
                                        }
                                        fullWidth
                                    />
                                </DA_InputWrapper>
                            </div>
                            {/* Frequency Selection */}
                            <div className={S.frequencyWrapper}>
                                <div className={S.frequencyBox}>
                                    <div className={S.frequencyBoxItem}>
                                        <DA_InputWrapper
                                            label={getDictionaryString("frequency")}
                                            forId="farm-in-time-frequency"
                                        >
                                            <DA_Select
                                                id="farm-in-time-frequency"
                                                value={currentFrequencyOption?.value}
                                                options={frequencyOptions}
                                                onChange={onFrequencySelectChanged}
                                                fullWidth
                                            />
                                        </DA_InputWrapper>
                                    </div>
                                    {currentFrequency === "Yearly" && (
                                        <div className={S.frequencyBoxItem}>
                                            <DA_InputWrapper
                                                label={getDictionaryString("siloAndHouses.month")}
                                                forId="farm-in-time-month"
                                            >
                                                <DA_Select
                                                    id="farm-in-time-month"
                                                    value={currentMonthOption?.value}
                                                    options={monthOptions}
                                                    invalid={hasMonthError}
                                                    onChange={onMonthSelectChanged}
                                                    placeholder={getDictionaryString("choose")}
                                                    fullWidth
                                                />
                                            </DA_InputWrapper>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className={S.buttonWrapper}>
                <DA_Button
                    title={
                        isMobile
                            ? getDictionaryString("FarmInTimeModalMobileSubmitButton")
                            : getDictionaryString("FarmInTimeModalSubmitButton")
                    }
                    onClick={onSubmitButtonClicked}
                    className={S.primary}
                    disabled={!isFormReady}
                />
                <DA_Button
                    title={getDictionaryString("cancel")}
                    isGhost
                    onClick={closeModal}
                    className={S.secondary}
                />
            </div>

            {/* Address Form Modal - Rendered via portal to ensure proper stacking */}
            {showAddressModal &&
                createPortal(
                    <div role="dialog" aria-modal="true" className={S.addressModalOverlay}>
                        <div role="document" className={S.addNewModalWrapper}>
                            <div className={S.addNewModal}>
                                <h3>{getDictionaryString("siloAndHouses.addNewAddress")}</h3>
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setShowAddressModal(false);
                                    }}
                                    className={S.closeBTn}
                                    aria-label="Close address form"
                                >
                                    <DA_Icon name={DA_IconNames.Close} className={S.closeIcon} />
                                </button>
                            </div>
                            <div className={S.contentBody}>
                                <DA_AddressForm
                                    onSubmit={onAddressFormSubmitted}
                                    onCancel={() => setShowAddressModal(false)}
                                    submitText={getDictionaryString("addAddress")}
                                    isModalMobile={isMobile && true}
                                />
                            </div>
                        </div>
                    </div>,
                    document.body
                )}
        </DA_Modal>
    );
};
